import React, { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'

interface Props {
  bundledOffers: OfferPageState['bundledOffers']
  isDatePickerOpen: boolean
  bundleSwapModalTicker: number
}

function BundleAndSaveSwapModal({
  bundledOffers,
  isDatePickerOpen,
  bundleSwapModalTicker,
}: Props) {
  const intl = useIntl()
  const [firstHotelOffer, secondHotelOffer] = useMemo(
    () => Object.values(bundledOffers ?? {}),
    [bundledOffers],
  )

  const {
    on: openReverseModal,
    off: closeModal,
    value: isModalOpen,
  } = useToggle(false)

  const dispatch = useOfferPageDispatch()

  const swapOrder = useCallback(() => {
    const firstOfferId = firstHotelOffer.offer?.id
    const secondOfferId = secondHotelOffer.offer?.id

    if (!firstOfferId || !secondOfferId) {
      return
    }

    if (firstHotelOffer.checkInDate || secondHotelOffer.checkInDate) {
      showSnackbar(intl.formatMessage({
        id: 'twZnKsWi/B',
        defaultMessage: 'The order of hotels has been updated. Please select your dates to continue.',
        description: 'Snackbar confirmation after swapping bundle hotel order',
      }), 'success')
    }
    dispatch({ type: OfferPageActions.resetDates, offerId: secondOfferId })
    dispatch({ type: OfferPageActions.resetDates, offerId: firstOfferId })
    dispatch({ type: OfferPageActions.reverseBundles })
    dispatch({
      type: OfferPageActions.setPaginatorCurrentOfferId,
      currentOfferId: secondOfferId,
    })

    if (!isDatePickerOpen) {
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }

    closeModal()
  }, [dispatch, firstHotelOffer, secondHotelOffer, isDatePickerOpen, closeModal, intl])

  useEffect(() => {
    if (bundleSwapModalTicker) {
      openReverseModal()
    }
  }, [bundleSwapModalTicker, openReverseModal])

  const swapModalTitle = intl.formatMessage({
    id: 'Ev0wqUPgvo',
    defaultMessage: 'Swap order of hotels?',
    description: 'Title for modal to confirm swapping the order of hotels in a bundle',
  })

  const swapModalConfirm = intl.formatMessage({
    id: 'bg499FkOb2',
    defaultMessage: 'Yes, swap order',
    description: 'Confirm button to swap the order of hotels in a bundle',
  })

  const swapModalCancel = intl.formatMessage({
    id: 'x7W22QF+Ma',
    defaultMessage: 'Cancel',
    description: 'Cancel button in the swap hotel order modal',
  })

  if (!bundledOffers) return null

  return (
    <div>
      <Modal
        title={swapModalTitle}
        isOpen={isModalOpen}
        primaryActionText={swapModalConfirm}
        secondaryActionText={swapModalCancel}
        onPrimaryActionClick={swapOrder}
        onSecondaryActionClick={closeModal}
        onClose={closeModal}
      >
        <BodyText variant="large" colour="neutral-two">
          {intl.formatMessage({
            id: '13xXh7RCxB',
            defaultMessage: 'The new order of your bundle will be:',
            description: 'Description text before listing the new hotel order in swap modal',
          })}
        </BodyText>
        <BodyText variant="large" colour="neutral-two">
          {intl.formatMessage({
            id: 'mp46SoNjff',
            defaultMessage: '1. {hotelName}',
            description: 'First hotel in the new bundle order after swap',
          }, { hotelName: secondHotelOffer.offer?.property?.name })}
        </BodyText>
        <BodyText variant="large" colour="neutral-two">
          {intl.formatMessage({
            id: '/kJWwxuGzM',
            defaultMessage: '2. {hotelName}',
            description: 'Second hotel in the new bundle order after swap',
          }, { hotelName: firstHotelOffer.offer?.property?.name })}
        </BodyText>
        <br />
        <BodyText variant="large">
          {intl.formatMessage({
            id: 'AH7P8xKj/S',
            defaultMessage: 'Once confirmed you will need to re-select the dates for each hotel.',
            description: 'Warning text about needing to reselect dates after swapping hotel order',
          })}
        </BodyText>
      </Modal>
    </div>
  )
}

export default withOfferPageState((state) => {
  return {
    bundledOffers: state.bundledOffers,
    isDatePickerOpen: state.activeFilter === 'checkin',
    bundleSwapModalTicker: state.bundleSwapModalTicker,
  }
}, BundleAndSaveSwapModal)
